<template>
  <div class="tw:flex tw:flex-1 tw:flex-col tw:gap-4 tw:overflow-auto tw:p-4">
    <div class="tw:columns-2">
      <div>
        <Radio
          v-model="keywordsType"
          dense
          :label="getLocaleText('stream_filters_keywords.keywords')"
          new-design
          value="keywords"
        />
      </div>
      <div>
        <Radio
          v-model="keywordsType"
          dense
          :label="getLocaleText('stream_filters_keywords.advanced_keywords')"
          new-design
          value="advanced-keywords"
        />
      </div>
    </div>

    <div
      v-if="keywordsType === 'keywords'"
      class="tw:flex tw:flex-col tw:gap-4"
    >
      <div>
        <InputText
          v-model="keywords"
          :label="getLocaleText('stream_filters_keywords.keywords')"
          new-design
        />
        <div class="field-subtext tw:mt-2">
          {{ getLocaleText("stream_filters_keywords.comma_separated") }}
        </div>
      </div>
      <div>
        <InputText
          v-model="excludedKeywords"
          :label="getLocaleText('stream_filters_keywords.excluded_keywords')"
          new-design
        />
        <div class="field-subtext tw:mt-2">
          {{ getLocaleText("stream_filters_keywords.comma_separated") }}
        </div>
      </div>
    </div>

    <div>
      <template v-if="keywordsType === 'advanced-keywords'">
        <div class="tw:font-bold tw:uppercase tw:text-primary">
          {{ getLocaleText("stream_filters_keywords.keywords_title") }}
        </div>
        <SuggestedKeywordsBoolean
          v-model:simple-boolean="localSimpleBoolean"
          class="tw:mt-4"
        />

        <BaseExpansionPanel
          class="tw:mt-4"
          no-padding
        >
          <template #header="{ toggle }">
            <div
              class="tw:flex-1 tw:font-bold tw:uppercase tw:text-primary"
              @click="toggle"
            >
              {{
                getLocaleText("stream_filters_keywords.boolean_query_preview")
              }}
            </div>
          </template>

          <QueryEditor
            class="tw:mt-2"
            disable-count
            hide-help-button
            hide-label
            :max-height="594"
            :min-height="30"
            :model-value="booleanQuery"
            preview
            read-only
          />

          <div class="field-subtext tw:mt-2">
            {{
              getLocaleText(
                "stream_filters_keywords.boolean_query_preview_description"
              )
            }}
          </div>
        </BaseExpansionPanel>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import { computed, provide, ref, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import SuggestedKeywordsBoolean from "shared/components/AI/SuggestedKeywords/SuggestedKeywordsBoolean.vue";
import { BaseExpansionPanel, InputText } from "shared/components/base";
import { SimpleBooleanOverrideTooltipSymbol } from "shared/components/base/symbols";
import Radio from "shared/components/core/forms/Radio.vue";
import QueryEditor from "shared/components/QueryEditor/QueryEditor.vue";
import useSimpleBooleanParams from "shared/composables/useSimpleBooleanParams";
import { type SimpleBoolean } from "shared/resources/Stream";
import type { Nullable } from "shared/types";

const keywords = defineModel<string>("keywords", {
  default: "",
});

const excludedKeywords = defineModel<string>("excludedKeywords", {
  default: "",
});

const simpleBoolean = defineModel<Nullable<SimpleBoolean>>("simpleBoolean", {
  default: null,
});

const booleanQuery = defineModel<string>("booleanQuery", {
  default: "",
});

const localSimpleBoolean = computed<SimpleBoolean>({
  get() {
    if (simpleBoolean.value) {
      return simpleBoolean.value;
    }

    return {
      must: [],
      should: [],
      must_not: [],
    };
  },
  set(value) {
    simpleBoolean.value = value;
  },
});

const keywordsType = ref<"keywords" | "advanced-keywords">(
  simpleBoolean.value ? "advanced-keywords" : "keywords"
);

watch(keywordsType, () => {
  keywords.value = "";
  excludedKeywords.value = "";
  simpleBoolean.value = null;
  booleanQuery.value = "";
});

const { simpleBooleanParams } = useSimpleBooleanParams();

async function updateBooleanQuery() {
  if (!localSimpleBoolean.value.must.length) {
    booleanQuery.value = "";

    return;
  }

  try {
    const params = {
      simple_boolean_query: simpleBooleanParams(simpleBoolean.value),
    };

    const response = await $streemApiV1.post(
      "streams/convert_simple_boolean_query",
      {
        params,
      }
    );

    booleanQuery.value = response.data.boolean_query;
  } catch (error) {
    ErrorDialog(
      getLocaleText("stream_filters_keywords.simple_boolean_error"),
      error
    );
  }
}

const debouceUpdateBooleanQuery = debounce(updateBooleanQuery, 300);

watch(
  simpleBoolean,
  () => {
    debouceUpdateBooleanQuery();
  },
  { deep: true, immediate: true }
);

provide(SimpleBooleanOverrideTooltipSymbol, true);
</script>
